// GR01 일반입고 저장/확정/엑셀 — 2.0 API 전용. 1.0 God endpoint(/api/grid/save, /api/internal/data/send)를 부르지 않는다.
// 1.0 파리티 근거: FilterBar.onSave 가 EDITTED 값으로 1.0(/api/grid/save)과 2.0(/api/v1/request-inbound)을
// 갈라 호출했다. 2.0 전환에서는 저장 전량이 request-inbound 한 경로로 모인다.
import { useMutation, UseMutationResult } from '@tanstack/react-query'
import request, { getExcel } from '@/utils/request'
import { menuCodeHeaders } from '@/utils/menuCodeHeaders'
import { GeneralInboundParams, GeneralInboundRow, toSearchBody } from './useGetInboundList'
import { requiredFieldsFor } from './requiredFields'

/** 입고유형 기본값 — 1.0 InboundUtil.mapInboundRequestFields 가 GR01 은 '일반' 로 고정한다 */
export const DEFAULT_ORDER_TYPE = '일반'

interface InboundItemBody {
  id?: number
  plannedDate: string | null
  orderType: string
  orderOptionNumber: string | null
  warehouseName: string | null
  warehouseArea: string | null
  styleNumber: string | null
  barcode: string
  /** NIKE_IDENTIFIERS_FLAG 가 꺼지면 싣지 않는다(복원 전 본문과 같게) */
  uid?: string | null
  optionCode?: string | null
  quantity: number
  brandName: string | null
  purchasePrice: number
  apiFlag: boolean
  flag: boolean
  companyName: string | null
  goods: {
    goodsGroup: string | null
    goodsName: string
    goodsPrice: number
  }
  transfer: { invoiceNumber: string | null }
  lot: { lot5: string | null }
}

interface RequestInboundBody {
  shipper: string
  items: InboundItemBody[]
}

/** 'yyyy-MM-dd' → 'yyyyMMdd' (BE @JsonFormat DatePatterns.YYYYMMDD) */
const toPlannedDate = (value: string): string | null => {
  const digits = value.replace(/[^0-9]/g, '')
  return digits.length === 8 ? digits : null
}

const blankToNull = (value: string): string | null => (value.trim() ? value.trim() : null)

/** 1.0 InboundUtil.FIELD_MAP 과 같은 대응. AP_PRICE 는 purchasePrice·goods.goodsPrice 둘 다에 쓴다 */
export const toItemBody = (row: GeneralInboundRow, nikeIdentifiers = true): InboundItemBody => ({
  ...(row._isNew ? {} : { id: row._id }),
  plannedDate: toPlannedDate(row.PLN_DATE),
  orderType: row.ORD_TYPE || DEFAULT_ORDER_TYPE,
  orderOptionNumber: blankToNull(row.ORD_OPT_NO),
  warehouseName: blankToNull(row.WH_NM),
  warehouseArea: blankToNull(row.WH_AREA),
  styleNumber: blankToNull(row.STL_NO),
  barcode: row.BARCODE.trim(),
  ...(nikeIdentifiers ? { uid: blankToNull(row.UID), optionCode: blankToNull(row.option_code) } : {}),
  quantity: Number(row.ORD_QTY ?? 0),
  brandName: blankToNull(row.BRD_NM),
  purchasePrice: Number(row.AP_PRICE ?? 0),
  apiFlag: false,
  flag: false,
  companyName: blankToNull(row.COM_NM),
  goods: {
    goodsGroup: blankToNull(row.GDS_GROUP1),
    goodsName: row.GDS_NM.trim(),
    goodsPrice: Number(row.AP_PRICE ?? 0),
  },
  transfer: { invoiceNumber: blankToNull(row.TRS_INVOICE_NO) },
  // 거래유형 — 온라인은 원장에 null 로 들어간다(1.0 GR01.onClickChange 와 동일 규약)
  lot: { lot5: blankToNull(row.LOT_ATTR5) },
})

/** 빈 값 판정 — 편집 셀은 지우면 빈 문자열이 된다. 수량은 Number('') 가 0 이라 따로 걸러야 한다 */
const isBlank = (value: unknown) => value == null || String(value).trim() === ''

/** 필수 컬럼 중 빈 칸이 있는가 — 저장 전 검사(validateRows)와 표의 빨간 행이 같은 기준을 쓴다 */
export const hasMissingRequired = (row: GeneralInboundRow, nikeIdentifiers = true): boolean =>
  requiredFieldsFor(row.SPR_NM, nikeIdentifiers).some(({ key }) => isBlank(row[key]))

/** 받침 유무에 따라 주격조사를 고른다 — '상품명이', '바코드가' */
const subjectParticle = (word: string) => {
  // UID는 한국어로 '아이디'라고 읽어 받침이 없다. 영문 필드에 기계적인 '이(가)'를 붙이지 않는다.
  if (/^[A-Za-z0-9]+$/.test(word)) return '가'
  const last = word.charCodeAt(word.length - 1)
  const isHangulSyllable = last >= 0xac00 && last <= 0xd7a3
  if (!isHangulSyllable) return '이(가)'
  return (last - 0xac00) % 28 === 0 ? '가' : '이'
}

/** 저장 전 필수값 검사 — BE @NotBlank/@NotNull 로 400 이 나기 전에 행을 짚어준다 */
export const validateRows = (rows: GeneralInboundRow[], nikeIdentifiers = true): string[] =>
  rows.reduce<string[]>((acc, row, index) => {
    // 공백만 든 값은 isBlank 가 빈 값으로 보는데 JS 에서는 truthy 다. 같은 기준으로 맞추지 않으면
    // 앞에 공백만 붙은 "  : 입고번호가 비어 있습니다." 같은 문구가 나온다.
    const label = row.ORD_OPT_NO.trim() || row.BARCODE.trim() || `${index + 1}번째 행`
    requiredFieldsFor(row.SPR_NM, nikeIdentifiers).forEach(({ key, label: field }) => {
      if (isBlank(row[key])) acc.push(`${label}: ${field}${subjectParticle(field)} 비어 있습니다.`)
    })
    if (!isBlank(row.ORD_QTY) && Number.isNaN(Number(row.ORD_QTY))) acc.push(`${label}: 수량이 숫자가 아닙니다.`)
    // 입고단가도 같이 본다. NaN 이 나가면 JSON 에서 null 이 되는데 BE 는 non-null BigDecimal 이라
    // 400 만 돌아오고 어느 행인지 못 짚는다. 수량만 짚어주던 비대칭을 없앤다.
    if (!isBlank(row.AP_PRICE) && Number.isNaN(Number(row.AP_PRICE))) acc.push(`${label}: 입고단가가 숫자가 아닙니다.`)
    return acc
  }, [])

/**
 * 화주 혼재 판정 — 섞여 있으면 화주코드 목록, 아니면 빈 배열 (결정 09).
 *
 * BE 요청 본문이 화주 단건이라 섞인 저장은 화주별 순차 POST 로 나간다. 첫 화주가 성공하고
 * 두 번째가 실패하면 화면은 전체 실패로 보이고 재조회도 하지 않는다. 사용자가 다시 저장하면
 * 기존 행은 items[].id 로 갱신이라 멱등하지만 엑셀 신규 행은 id 가 없어 생성 요청이 다시 나가
 * 중복 입고요청이 들어간다. 화주는 조회 조건이자 요청 본문의 단건 키라 화면에서 섞을 이유가 없다.
 * 저장 전에 막는다.
 */
export const mixedShippers = (rows: GeneralInboundRow[]): string[] => {
  const codes = [...new Set(rows.map((row) => row.SPR_NM))]
  return codes.length > 1 ? codes : []
}

/** 화주코드 기준 묶음 — BE 요청 본문이 shipper 단건이라 화주가 섞이면 갈라 보낸다 */
const groupByShipper = (rows: GeneralInboundRow[]): Map<string, GeneralInboundRow[]> =>
  rows.reduce((map, row) => {
    const key = row.SPR_NM
    map.set(key, [...(map.get(key) ?? []), row])
    return map
  }, new Map<string, GeneralInboundRow[]>())

/** SKU 미매핑 차단 — HTTP 422 + meta.errorCode=ERROR_INBOUND_SKU_UNMAPPED (LOGISPRD-9000 FE 계약) */
export const SKU_UNMAPPED_CODE = 'ERROR_INBOUND_SKU_UNMAPPED'

export interface SkuUnmappedError {
  message: string
  failedBarcodes: string[]
}

export const asSkuUnmapped = (error: unknown): SkuUnmappedError | null => {
  const response = (
    error as {
      response?: { status?: number; data?: { meta?: { errorCode?: string; message?: string }; data?: { failedBarcodes?: string[] } } }
    }
  )?.response
  if (response?.status !== 422 || response?.data?.meta?.errorCode !== SKU_UNMAPPED_CODE) return null
  return {
    message: response.data?.meta?.message ?? 'SKU 정보를 찾을 수 없어 저장에 실패했습니다.',
    failedBarcodes: response.data?.data?.failedBarcodes ?? [],
  }
}

/**
 * 저장·확정 상호 잠금용 mutation key.
 *
 * 저장이 아직 안 끝난 상태에서 확정이 나가면, 반영되지 않은 값으로 입고플랜과 컷오프 차수가 만들어지고
 * 그대로 실행계(MWMS)로 입고지시가 나간다. 되돌릴 수 없고, 선택 없이 확정하면 화주 전건이라 범위도 크다.
 * 반대 방향도 같다 — 확정이 도는 중의 저장은 확정 대상 행을 바꾼다.
 * 두 버튼이 서로의 진행 상태를 봐야 하는데 컴포넌트가 갈려 있어 mutation key 로 잇는다.
 */
export const GR01_SAVE_MUTATION_KEY = ['GR01', 'SAVE_INBOUNDS'] as const
export const GR01_CONFIRM_MUTATION_KEY = ['GR01', 'CONFIRM_INBOUNDS'] as const

/** 저장 — POST /api/v1/request-inbound. 기존 행은 items[].id 로 갱신, 엑셀 업로드 신규 행은 id 없이 생성 */
export const useSaveInbounds = (nikeIdentifiers = true): UseMutationResult<number, Error, GeneralInboundRow[]> =>
  useMutation({
    mutationKey: GR01_SAVE_MUTATION_KEY,
    mutationFn: async (rows: GeneralInboundRow[]) => {
      for (const [shipper, group] of groupByShipper(rows)) {
        await request.post<ApiResponseV2<unknown>, RequestInboundBody>(
          '/api/v1/request-inbound',
          { shipper, items: group.map((row) => toItemBody(row, nikeIdentifiers)) },
          // request-inbound 는 gr07 과 rgr16 이 같이 부른다 — 감사 로그 화면코드를 헤더로 보낸다(LOGISPRD-15996)
          menuCodeHeaders('GR07'),
        )
      }
      return rows.length
    },
  })

export interface ConfirmTarget {
  /** 선택이 없을 때 쓰는 화주코드 — 조회 조건의 화주다 */
  shipperCode: string
  /** 확정 대상 행. 비면 BE 가 그 화주의 미확정 전건을 확정한다 */
  rows: GeneralInboundRow[]
}

/**
 * 확정 — POST /api/v1/confirm-inbound. 1.0 FilterBar.onSend 의 GR01 분기와 같은 계약.
 *
 * ids 를 비워 보내면 BE(NormalInboundPlanService.create)가 그 화주·입고유형의 전건을 확정한다.
 * 1.0 도 선택 없이 확정하면 같은 동작이었다(`ids: data.map(...)` 이 빈 배열). 파리티를 위해 유지한다.
 */
export const useConfirmInbounds = (): UseMutationResult<number, Error, ConfirmTarget> =>
  useMutation({
    mutationKey: GR01_CONFIRM_MUTATION_KEY,
    mutationFn: async ({ shipperCode, rows }: ConfirmTarget) => {
      if (!rows.length) {
        await request.post<ApiResponseV2<{ items: number[] }>, { shipper: string; ids: number[] }>('/api/v1/confirm-inbound', {
          shipper: shipperCode,
          ids: [],
        })
        return 0
      }
      // shipper 는 조회 조건의 화주로 고정한다. 행의 SPR_NM 은 편집 셀이라 저장 전 값이 섞일 수 있고,
      // BE 는 ids 로만 행을 찾고 shipper 로 거르지 않는다(NormalInboundPlanService.create).
      // 그 값이 그대로 컷오프 차수와 실행계 이벤트의 화주가 되므로, 다른 화주 행이 섞이면 아예 막는다.
      const mismatched = [...new Set(rows.map((row) => row.SPR_NM).filter((code) => code !== shipperCode))]
      if (mismatched.length) {
        throw new Error(`조회한 화주(${shipperCode})와 다른 행이 섞여 있습니다 (${mismatched.join(', ')}). 저장 후 다시 조회하고 확정하세요.`)
      }
      await request.post<ApiResponseV2<{ items: number[] }>, { shipper: string; ids: number[] }>('/api/v1/confirm-inbound', {
        shipper: shipperCode,
        ids: rows.map((row) => row._id),
      })
      return rows.length
    },
  })

export const GR01_DELETE_MUTATION_KEY = ['GR01', 'DELETE_INBOUNDS'] as const

/**
 * 삭제 — POST /api/v1/normal-inbounds/delete. 응답 affectedRows 는 실제 지운 건수다.
 *
 * shipperCode 는 조회 조건의 화주다. BE 는 ids 와 shipperCode 를 함께 걸러 지우므로
 * 다른 화주 행이 섞이면 그 행은 조용히 남는다 — 건수는 affectedRows 로 알린다.
 */
export const useDeleteInbounds = (): UseMutationResult<number, Error, { shipperCode: string; ids: number[] }> =>
  useMutation({
    mutationKey: GR01_DELETE_MUTATION_KEY,
    mutationFn: async (body) => {
      const res = await request.post<ApiResponseV2<{ affectedRows: number }>, { shipperCode: string; ids: number[] }>(
        '/api/v1/normal-inbounds/delete',
        body,
      )
      return res.data?.affectedRows ?? 0
    },
  })

/**
 * 엑셀 다운로드 — POST /api/v1/get-normal-inbounds/excel (서버 xlsx, BE @AccessAudit 이 EXPORT 감사기록).
 *
 * 사유는 body 가 아니라 쿼리 파라미터 `reasonCode`·`detailedReason` 로 나눠 보낸다 — BE 가
 * `{사유코드}_{상세사유}` 로 합쳐 감사로그 remark 에 남긴다(combineReasonCode). 미리 합치면 코드가 두 번 들어간다.
 */
export const downloadInboundsExcel = async (
  params: GeneralInboundParams,
  { reasonCode, reasonDetail }: { reasonCode: string; reasonDetail: string },
): Promise<void> => {
  const reasonQuery = new URLSearchParams({ reasonCode, detailedReason: reasonDetail })
  const res = await getExcel(`/api/v1/get-normal-inbounds/excel?${reasonQuery}`, { method: 'post', data: toSearchBody(params) })
  const disposition = String(res.headers?.['content-disposition'] ?? '')
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition)
  const fileName = match ? decodeURIComponent(match[1]) : '일반입고.xlsx'
  const anchor = document.createElement('a')
  anchor.href = URL.createObjectURL(res.data)
  anchor.download = fileName
  anchor.click()
  // click() 은 다운로드를 예약만 하고 Blob 읽기는 비동기다. 같은 틱에 해제하면 Firefox 에서
  // 빈 파일로 떨어진다. 현재 실행 큐를 비운 뒤 회수한다.
  setTimeout(() => URL.revokeObjectURL(anchor.href), 0)
}
